import { c as _c } from "react/compiler-runtime";
import { useIntl } from '@onefootprint/hooks';
import { IcoChevronRight16 } from '@onefootprint/icons';
import type { EntityOnboarding } from '@onefootprint/request-types/dashboard';
import { LinkButton, Tabs } from '@onefootprint/ui';
import { useQuery } from '@tanstack/react-query';
import { type CSSProperties, Fragment, type ReactNode, type Ref, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useEventListener } from 'usehooks-ts';
import { BUSINESS_INSIGHT_SUBSECTIONS, CardError, CardSkeleton, EncryptedBusinessInsights, getOnboardingQueries, getRelevantDis, groupRiskSignals, isAwaitingResults, ObeRules, OnboardingDocuments, OnboardingFieldValidations, OnboardingRiskSignals, OnboardingRules, OnboardingVaultData, Subsection as SubsectionBody, type SubsectionType } from '@/components/entity-onboarding-details';
import useEntityVault from '@/entities/hooks/use-entity-vault';
import type { WithEntityProps } from '@/entity/components/with-entity';
import DeviceInsights from '../../../device-insights/device-insights';
import DuplicateData from '../../../duplicate-data';
import useShowOnboarding from '../../../onboardings-shared/hooks/use-show-onboarding';
import { pinScrollPosition } from '../../../onboardings-shared/utils';
import OtherInsights from '../../../other-insights';
import type { SectionKey } from '../../onboardings.types';
import OnboardingHeader from '../onboarding-header';
import OnboardingSection, { SECTION_ICONS } from '../onboarding-section';
import BusinessInsights from './components/business-insights';
import TabBarActions from './components/tab-bar-actions';
import useDecryptAll from './hooks/use-decrypt-all';
import useSectionCounts from './hooks/use-section-counts';
import useSections from './hooks/use-sections';
type LiveSectionKey = Extract<SectionKey, 'duplicate-data' | 'device-insights' | 'other-insights'>;
type TabKey = 'data' | 'rules' | 'business' | 'devices';

// Links name sections as the classic layout does, which splits business insights into its parts.
const toLinkedSection = (key: SectionKey): SubsectionType => key === 'business-insights' ? 'business-details' : key;
const isLinkedSection = (key: SectionKey, linked: SubsectionType | null) => key === 'business-insights' ? BUSINESS_INSIGHT_SUBSECTIONS.some(section => section === linked) : key === linked;
const TABS: {
  key: TabKey;
  sections: SectionKey[];
}[] = [{
  key: 'rules',
  sections: ['rules', 'risk-signals', 'field-validations']
}, {
  key: 'business',
  sections: ['business-insights']
}, {
  key: 'devices',
  sections: ['device-insights', 'other-insights']
},
// Documents are a card in the data section, but links still name them and must open this tab.
{
  key: 'data',
  sections: ['user-data', 'business-data', 'documents', 'duplicate-data']
}];
type OnboardingDataProps = WithEntityProps & {
  onboarding: EntityOnboarding;
  onViewCurrent?: (key: LiveSectionKey) => void;
  sectionRef?: (key: SectionKey) => Ref<HTMLElement>;
};
const OnboardingData = t0 => {
  const $ = _c(63);
  const {
    entity,
    onboarding,
    onViewCurrent,
    sectionRef
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "onboardings"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("entity-details", t1);
  const {
    formatDateWithTime
  } = useIntl();
  let t2;
  if ($[1] !== entity || $[2] !== onboarding) {
    t2 = getOnboardingQueries(entity, onboarding);
    $[1] = entity;
    $[2] = onboarding;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const queries = t2;
  const {
    data: riskSignals,
    error: riskSignalsError
  } = useQuery(queries.riskSignals);
  const {
    data: entityAttributes,
    isPending: isAttributesPending,
    error: attributesError
  } = useQuery(queries.attributes);
  const {
    data: onboardingTrace,
    error: traceError
  } = useQuery(queries.trace);
  let t3;
  if ($[4] !== entity.id || $[5] !== entityAttributes || $[6] !== onboarding.seqno) {
    t3 = {
      entityAttributes,
      entityId: entity.id,
      seqno: onboarding.seqno
    };
    $[4] = entity.id;
    $[5] = entityAttributes;
    $[6] = onboarding.seqno;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const entityVault = useEntityVault(t3);
  const sections = useSections(onboarding);
  const counts = useSectionCounts(entity, onboarding);
  const {
    onboardingSection: requestedSection,
    setOnboardingSection
  } = useShowOnboarding();
  const tabTitles = {
    data: t(_temp),
    rules: t(_temp2),
    business: t(_temp3),
    devices: t(_temp4)
  };
  const tabs = TABS.map(t4 => {
    const {
      key,
      sections: keys
    } = t4;
    return {
      key,
      title: tabTitles[key],
      sections: sections.filter(section => keys.includes(section.key))
    };
  }).filter(_temp5);
  let t5;
  if ($[8] !== requestedSection) {
    t5 = TABS.find(t6 => {
      const {
        sections: keys_0
      } = t6;
      return keys_0.some(key_0 => isLinkedSection(key_0, requestedSection));
    });
    $[8] = requestedSection;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const requestedTab = t5;
  const tab_0 = tabs.find(candidate => candidate.key === requestedTab?.key) ?? tabs[0];
  const tabBarRef = useRef(null);
  const tabPanelRef = useRef(null);
  const isResettingScrollRef = useRef(false);
  const [tabBarHeight, setTabBarHeight] = useState();
  const [isTabBarStuck, setIsTabBarStuck] = useState(false);
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => (tabPanelRef.current?.getBoundingClientRect().top ?? 0) < (tabBarRef.current?.getBoundingClientRect().bottom ?? 0);
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const isPanelUnderTabs = t6;
  let t7;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => setIsTabBarStuck(isPanelUnderTabs());
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      capture: true,
      passive: true
    };
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  useEventListener("scroll", t7, undefined, t8);
  let t10;
  let t9;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      const tabBar = tabBarRef.current;
      if (!tabBar) {
        return;
      }
      const observer = new ResizeObserver(t11 => {
        const [entry] = t11;
        return setTabBarHeight(entry.borderBoxSize[0]?.blockSize);
      });
      observer.observe(tabBar);
      return () => observer.disconnect();
    };
    t10 = [];
    $[13] = t10;
    $[14] = t9;
  } else {
    t10 = $[13];
    t9 = $[14];
  }
  useEffect(t9, t10);
  const selectTab = key_1 => {
    const next = tabs.find(candidate_0 => candidate_0.key === key_1);
    if (!next) {
      return;
    }
    isResettingScrollRef.current = isPanelUnderTabs();
    setOnboardingSection(toLinkedSection(next.sections[0].key));
  };
  useLayoutEffect(() => {
    const panel = tabPanelRef.current;
    const tabBar_0 = tabBarRef.current;
    if (!isResettingScrollRef.current || !panel || !tabBar_0) {
      return;
    }
    isResettingScrollRef.current = false;
    return pinScrollPosition(panel, tabBar_0.getBoundingClientRect().bottom);
  }, [tab_0.key]);
  let t11;
  if ($[15] !== entityAttributes) {
    t11 = entityAttributes?.some(_temp6);
    $[15] = entityAttributes;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  const hasDecryptableDIs = Boolean(t11);
  let t12;
  if ($[17] !== entityAttributes || $[18] !== entityVault || $[19] !== hasDecryptableDIs) {
    t12 = hasDecryptableDIs && getRelevantDis((entityAttributes ?? []).filter(_temp7).map(_temp8)).every(di => entityVault.isDecrypted(di));
    $[17] = entityAttributes;
    $[18] = entityVault;
    $[19] = hasDecryptableDIs;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  const isVaultDecrypted = t12;
  const showVaultDecrypt = hasDecryptableDIs ? !isVaultDecrypted : Boolean(entityAttributes?.length);
  const canDecryptVault = showVaultDecrypt && hasDecryptableDIs;
  let t13;
  if ($[21] !== canDecryptVault || $[22] !== isAttributesPending || $[23] !== showVaultDecrypt) {
    t13 = isAttributesPending ? undefined : {
      isNeeded: showVaultDecrypt,
      canDecrypt: canDecryptVault
    };
    $[21] = canDecryptVault;
    $[22] = isAttributesPending;
    $[23] = showVaultDecrypt;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== entity || $[26] !== entityVault || $[27] !== onboarding || $[28] !== t13) {
    t14 = {
      entity,
      onboarding,
      vault: entityVault,
      vaultDecrypt: t13
    };
    $[25] = entity;
    $[26] = entityVault;
    $[27] = onboarding;
    $[28] = t13;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  const decrypt = useDecryptAll(t14);
  const asOfNote = onboarding.completedAt ? t(_temp9, {
    date: formatDateWithTime(new Date(onboarding.completedAt), {
      month: "short",
      year: "numeric"
    })
  }) : t(_temp10);
  let t15;
  if ($[30] !== onViewCurrent || $[31] !== t) {
    t15 = (note, liveKey) => <div className="flex flex-wrap items-center gap-x-3 gap-y-1"><p className="text-body-3 text-tertiary">{note}</p>{liveKey && onViewCurrent && <LinkButton variant="label-3" iconComponent={IcoChevronRight16} onClick={() => onViewCurrent(liveKey)}>{t(_temp11)}</LinkButton>}</div>;
    $[30] = onViewCurrent;
    $[31] = t;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  const renderMoment = t15;
  const getAction = key_2 => {
    switch (key_2) {
      case "duplicate-data":
      case "device-insights":
        {
          return renderMoment(asOfNote, onboarding.completedAt ? key_2 : undefined);
        }
      case "other-insights":
        {
          return renderMoment(t(_temp12), key_2);
        }
      default:
        {
          return null;
        }
    }
  };
  let t16;
  if ($[33] !== attributesError || $[34] !== entity || $[35] !== entityVault || $[36] !== hasDecryptableDIs || $[37] !== isAttributesPending || $[38] !== isVaultDecrypted || $[39] !== onboarding || $[40] !== onboardingTrace || $[41] !== riskSignals || $[42] !== riskSignalsError || $[43] !== sectionRef || $[44] !== traceError) {
    t16 = key_3 => {
      switch (key_3) {
        case "user-data":
        case "business-data":
          {
            return <div className="flex flex-col gap-3 p-5"><OnboardingVaultData entity={entity} vault={entityVault} isLoading={isAttributesPending} error={attributesError} /><OnboardingDocuments ref={sectionRef?.("documents")} entity={entity} onboarding={onboarding} /></div>;
          }
        case "rules":
          {
            if (!onboarding.hasGraphTrace) {
              return <OnboardingRules entity={entity} onboarding={onboarding} />;
            }
            if (onboardingTrace) {
              return <ObeRules trace={onboardingTrace} isAwaitingRules={isAwaitingResults(onboarding)} />;
            }
            if (traceError) {
              return <SubsectionBody><CardError error={traceError} /></SubsectionBody>;
            }
            return <SubsectionBody><CardSkeleton rows={1} /></SubsectionBody>;
          }
        case "risk-signals":
          {
            return <OnboardingRiskSignals riskSignals={riskSignals && groupRiskSignals(riskSignals)} error={riskSignalsError} />;
          }
        case "field-validations":
          {
            return <SubsectionBody><OnboardingFieldValidations entity={entity} onboarding={onboarding} /></SubsectionBody>;
          }
        case "duplicate-data":
          {
            return <SubsectionBody><DuplicateData timestamp={onboarding.completedAt} /></SubsectionBody>;
          }
        case "business-insights":
          {
            return isVaultDecrypted ? <BusinessInsights onboarding={onboarding} /> : <EncryptedBusinessInsights canDecrypt={hasDecryptableDIs} vaultData={entityVault.data} error={attributesError} />;
          }
        case "device-insights":
          {
            return <div className="p-5"><DeviceInsights entity={entity} asOf={onboarding.completedAt} /></div>;
          }
        case "other-insights":
          {
            return <SubsectionBody><OtherInsights onboardingId={onboarding.id} /></SubsectionBody>;
          }
      }
    };
    $[33] = attributesError;
    $[34] = entity;
    $[35] = entityVault;
    $[36] = hasDecryptableDIs;
    $[37] = isAttributesPending;
    $[38] = isVaultDecrypted;
    $[39] = onboarding;
    $[40] = onboardingTrace;
    $[41] = riskSignals;
    $[42] = riskSignalsError;
    $[43] = sectionRef;
    $[44] = traceError;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  const renderSection = t16;
  let t17;
  if ($[46] !== decrypt || $[47] !== onboarding) {
    t17 = <OnboardingHeader onboarding={onboarding} decrypt={decrypt} />;
    $[46] = decrypt;
    $[47] = onboarding;
    $[48] = t17;
  } else {
    t17 = $[48];
  }
  let t18;
  if ($[49] !== decrypt || $[50] !== isTabBarStuck || $[51] !== onboarding) {
    t18 = isTabBarStuck && <TabBarActions onboarding={onboarding} decrypt={decrypt} onRemovedWhileFocused={() => tabBarRef.current?.querySelector("[role=\"tab\"][aria-selected=\"true\"]")?.focus({
      preventScroll: true
    })} />;
    $[49] = decrypt;
    $[50] = isTabBarStuck;
    $[51] = onboarding;
    $[52] = t18;
  } else {
    t18 = $[52];
  }
  const t19 = tab_0.key;
  const t20 = "tabpanel";
  const t21 = tab_0.title;
  const t22 = "flex flex-col gap-8 px-5 pt-5 pb-6 transition-opacity duration-150 ease-out starting:opacity-0 motion-reduce:transition-none";
  let t23;
  if ($[53] !== tabBarHeight) {
    t23 = tabBarHeight ? {
      "--onboardings-tabs-height": `${tabBarHeight}px`
    } as CSSProperties : undefined;
    $[53] = tabBarHeight;
    $[54] = t23;
  } else {
    t23 = $[54];
  }
  const T0 = Fragment;
  const t24 = onboarding.id;
  const t26 = tab_0.sections.map(t25 => {
    const {
      key: key_5,
      title: title_0
    } = t25;
    return <OnboardingSection key={key_5} ref={sectionRef?.(key_5)} title={title_0} icon={SECTION_ICONS[key_5]} count={counts[key_5]} action={getAction(key_5)}>{renderSection(key_5)}</OnboardingSection>;
  });
  let t27;
  if ($[55] !== T0 || $[56] !== onboarding.id || $[57] !== t26) {
    t27 = <T0 key={t24}>{t26}</T0>;
    $[55] = T0;
    $[56] = onboarding.id;
    $[57] = t26;
    $[58] = t27;
  } else {
    t27 = $[58];
  }
  let t28;
  if ($[59] !== t23 || $[60] !== t27 || $[61] !== tab_0) {
    t28 = <div key={t19} ref={tabPanelRef} role={t20} aria-label={t21} className={t22} style={t23}>{t27}</div>;
    $[59] = t23;
    $[60] = t27;
    $[61] = tab_0;
    $[62] = t28;
  } else {
    t28 = $[62];
  }
  return <>{t17}<div ref={tabBarRef} className="z-10 -mt-1 bg-primary px-5 pt-1 md:sticky md:top-[calc(var(--onboardings-strip-height,4rem)-1.75rem)]"><Tabs value={tab_0.key} onChange={selectTab} options={tabs.map(_temp13)} />{t18}</div>{t28}</>;
};
export default OnboardingData;
function _temp($) {
  return $.tabs.data;
}
function _temp2($_0) {
  return $_0.tabs.rules;
}
function _temp3($_1) {
  return $_1.tabs.business;
}
function _temp4($_2) {
  return $_2.tabs.deviceInsights;
}
function _temp5(tab) {
  return tab.sections.length > 0;
}
function _temp6(attr) {
  return attr.isDecryptable;
}
function _temp7(attr_0) {
  return attr_0.isDecryptable;
}
function _temp8(attr_1) {
  return attr_1.identifier;
}
function _temp9($_3) {
  return $_3.snapshot.asOf;
}
function _temp10($_4) {
  return $_4.snapshot.unfinished;
}
function _temp11($_5) {
  return $_5.snapshot.viewCurrent;
}
function _temp12($_6) {
  return $_6.snapshot.fromOnboarding;
}
function _temp13(t0) {
  const {
    key: key_4,
    title
  } = t0;
  return {
    label: title,
    value: key_4
  };
}